import { useState, useEffect, useCallback } from 'react';
import { PageHeader, Badge } from '@/components/ui/PageHeader';
import { DataTable, Pagination, type Column } from '@/components/ui/DataTable';
import { SelectField } from '@/components/ui/FormFields';
import { AuthButton } from '@/components/ui/AuthButton';
import { useAuth } from '@/context/AuthContext';
import {
  listLibrary,
  type NostalgiaLibraryItem,
  type NostalgiaEra,
  ERA_OPTIONS,
  LIBRARY_CATEGORY_OPTIONS,
} from '@/api/nostalgia';

export default function LibraryList() {
  const { isAuthenticated } = useAuth();

  const [items, setItems] = useState<NostalgiaLibraryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Filters
  const [categoryFilter, setCategoryFilter] = useState('');
  const [eraFilter, setEraFilter] = useState('');

  const fetchItems = useCallback(async () => {
    if (!isAuthenticated) return;

    setIsLoading(true);
    try {
      const response = await listLibrary({
        page,
        limit: 20,
        category: categoryFilter || undefined,
        era: eraFilter as NostalgiaEra || undefined,
      });
      setItems(response.data);
      setTotalPages(response.meta.totalPages);
    } catch (error) {
      console.error('Failed to fetch library:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, page, categoryFilter, eraFilter]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const columns: Column<NostalgiaLibraryItem>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (item) => (
        <div className="flex items-center gap-3">
          {item.imageUrl && (
            <img src={item.imageUrl} alt="" className="w-10 h-10 rounded object-cover" />
          )}
          <div>
            <div className="font-medium text-gray-900">{item.name}</div>
            {item.description && (
              <div className="text-xs text-gray-500 truncate max-w-xs">{item.description}</div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      render: (item) => (
        <Badge variant="blue">{item.category.replace('_', ' ')}</Badge>
      ),
    },
    {
      key: 'era',
      header: 'Era',
      render: (item) => item.era ? (
        <Badge variant="purple">{item.era.replace('era_', '')}</Badge>
      ) : '-',
    },
    {
      key: 'tags',
      header: 'Tags',
      render: (item) => (
        <div className="flex flex-wrap gap-1">
          {item.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="px-1.5 py-0.5 text-xs bg-gray-100 rounded">
              {tag}
            </span>
          ))}
          {item.tags.length > 3 && (
            <span className="text-xs text-gray-500">+{item.tags.length - 3}</span>
          )}
        </div>
      ),
    },
    {
      key: 'isActive',
      header: 'Status',
      render: (item) => (
        <Badge variant={item.isActive ? 'green' : 'red'}>
          {item.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
  ];

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-2xl font-bold">Nostalgia Library</h1>
            <AuthButton />
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-yellow-800">Please login to view the library.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <PageHeader
            title="Nostalgia Library"
            subtitle="Curated content for boards (TV shows, snacks, music, etc.)"
            breadcrumbs={[
              { label: 'Nostalgia', href: '/lab/nostalgia' },
              { label: 'Library' },
            ]}
          />
          <AuthButton />
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border p-4 mb-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="w-48">
              <SelectField
                label="Category"
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setPage(1);
                }}
                options={LIBRARY_CATEGORY_OPTIONS}
                placeholder="All Categories"
              />
            </div>
            <div className="w-40">
              <SelectField
                label="Era"
                value={eraFilter}
                onChange={(e) => {
                  setEraFilter(e.target.value);
                  setPage(1);
                }}
                options={ERA_OPTIONS}
                placeholder="All Eras"
              />
            </div>
            <div className="flex-1" />
            <div className="text-sm text-gray-500">
              Library items are seeded via database. Use Prisma Studio for direct edits.
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border">
          <DataTable
            columns={columns}
            data={items}
            keyField="id"
            isLoading={isLoading}
            emptyMessage="No library items found."
          />
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </div>

        {/* Info */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-medium text-blue-900 mb-2">Adding Library Items</h3>
          <p className="text-sm text-blue-800">
            Library items are typically added via seed scripts or Prisma Studio.
            To add items programmatically, run:
          </p>
          <code className="block mt-2 text-xs bg-blue-100 p-2 rounded">
            cd apps/api && pnpm prisma studio
          </code>
        </div>
      </div>
    </div>
  );
}
